import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';

// Dialog — the generic centred modal shell the app was missing.
//
// Everything modal in the repo was previously either ConfirmDialog (hard-wired
// to a warning icon + confirm/cancel footer, not a content shell) or a native
// <dialog> + showModal(). The native route is a dead end for anything that may
// contain a Radix popover/select: the parent's aria-hidden umbrella and focus
// trap swallow the nested layer, which is exactly why ConfirmDialog was moved
// onto Radix (see the note in ConfirmDialog.tsx). New modals belong here.
//
// Same primitive and z-layer conventions as ui/sheet.tsx, laid out as a centred
// panel rather than an edge sheet.

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className = '', ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={`fixed inset-0 z-[70] bg-foreground/40 ${className}`}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  // Width cap. Detail views want more room than a confirm prompt, so this is a
  // prop rather than a constant.
  size?: 'md' | 'lg';
  // Set false for a modal whose own header supplies the close affordance.
  showClose?: boolean;
  closeLabel?: string;
}

const sizes: Record<'md' | 'lg', string> = {
  md: 'w-[min(32rem,calc(100vw-2rem))]',
  lg: 'w-[min(48rem,calc(100vw-2rem))]',
};

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className = '', size = 'md', showClose = true, closeLabel = 'Close', children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      // max-h + flex column so long content scrolls INSIDE the panel and the
      // header/footer stay put — never a page-level scroll behind the overlay.
      className={
        'fixed left-1/2 top-1/2 z-[71] flex max-h-[90vh] -translate-x-1/2 -translate-y-1/2 ' +
        `flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-2xl ${sizes[size]} ${className}`
      }
      {...props}
    >
      {children}
      {showClose && (
        <DialogPrimitive.Close asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={closeLabel}
            className="absolute right-3 top-3"
          >
            <X weight="bold" />
          </Button>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = 'DialogContent';

export function DialogHeader({ children }: { children: ReactNode }) {
  return <header className="shrink-0 border-b border-border px-6 py-4 pr-14">{children}</header>;
}

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className = '', ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={`font-heading text-lg font-bold leading-tight tracking-tight ${className}`}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className = '', ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={`mt-0.5 text-sm text-muted-foreground ${className}`}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export function DialogBody({ children }: { children: ReactNode }) {
  return <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>;
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-4">
      {children}
    </footer>
  );
}
